import { ReactElement, useCallback, useEffect, useState } from "react";
import { IonContent, IonIcon, IonLabel, IonPage, IonSpinner } from "@ionic/react";
// The dock renders as RAW ion-tab-bar/ion-tab-button web components (the same elements
// the live app renders) — @ionic/react's IonTabBar wrapper is a routeInfo-driven state
// machine that misbehaves outside IonTabs. The elements must be defined explicitly
// because no React wrapper triggers their lazy registration on this page.
import { defineCustomElement as defineIonTabBar } from "@ionic/core/components/ion-tab-bar.js";
import { defineCustomElement as defineIonTabButton } from "@ionic/core/components/ion-tab-button.js";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

defineIonTabBar();
defineIonTabButton();

// React 19 renders unknown tags as custom elements natively; TypeScript just needs
// the intrinsics declared (scoped to what the preview dock uses).
declare module "react" {
  namespace JSX {
    interface IntrinsicElements {
      "ion-tab-bar": React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
        "selected-tab"?: string;
      };
      "ion-tab-button": React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
        tab?: string;
        selected?: boolean;
      };
    }
  }
}
import useQuery from "../../hooks/useQuery";
import { useConfig } from "../../contexts/ConfigContext";
import { getApiContentPreview } from "../../services/study.service";
import { StudyContentType } from "../../types/welcome";
import DisplayContent from "../../components/molecules/DisplayContent";
import ResonanceLayout from "../../components/organisms/ResonanceContext/ResonanceLayout";
import { useResonanceLayout } from "../../components/organisms/ResonanceContext/ResonanceLayoutContext";
import { getStudyTabs } from "../../navigation/studyTabs";
import { useStudySlug } from "../../hooks/useStudySlug";
import { pageBackgroundOf } from "../../theme/pageStyle";

/**
 * No-login content page preview. Opened from the Diapason content editor's "Preview"
 * button via a signed ?token= (mirrors QuestionnairePreview): renders the REAL
 * participant presentation — ContentRenderer through the study theme, the authored
 * page background, and the read-aloud row — inside a phone-sized frame, so researchers
 * see exactly what a participant's phone will show. Nothing is persisted.
 */

// "Study default" (empty) lets the API pick the study's primary language; the rest let
// a researcher preview each translation (same set as QuestionnairePreview).
const PREVIEW_LANGS: { code: string; labelKey: string }[] = [
  { code: "", labelKey: "questionnaire_preview_lang_default" },
  { code: "fr", labelKey: "questionnaire_preview_lang_fr" },
  { code: "en", labelKey: "questionnaire_preview_lang_en" },
  { code: "es", labelKey: "questionnaire_preview_lang_es" },
  { code: "ar", labelKey: "questionnaire_preview_lang_ar" },
];

const ContentPreview = (): ReactElement => {
  const { t, i18n } = useTranslation();
  const query = useQuery();
  const token = query.get("token") ?? "";
  const { contentId } = useParams<{ contentId: string }>();
  const { study } = useConfig();
  // Same single-study nuance as QuestionnairePreview: the study id the token was signed
  // for travels in the URL; config's pinned id is only the fallback.
  const queryStudyId = Number(query.get("studyId"));
  const studyId =
    Number.isFinite(queryStudyId) && queryStudyId > 0 ? queryStudyId : study?.id;

  const [lang, setLang] = useState<string>("");
  const [content, setContent] = useState<StudyContentType | null>(null);
  const [loading, setLoading] = useState(true);
  const [invalid, setInvalid] = useState(false);
  const [error, setError] = useState(false);
  // Show the app chrome (header band + tab dock) around the content, so researchers
  // judge the page as participants will actually see it. Default on.
  const [withChrome, setWithChrome] = useState(true);

  const { setResonanceLayout } = useResonanceLayout();
  const studySlug = useStudySlug();
  // The REAL per-study tab set (same source as Tabs.tsx) — true chrome fidelity.
  const studyTabs = getStudyTabs(studySlug);

  // Configure the REAL ResonanceLayout for the framed preview: the content's label as
  // the header title (t() returns non-keys verbatim), the authored page background,
  // and no menu burger (the preview has no authenticated session behind it). Runs for
  // loading/error states too — otherwise the frame shows the context defaults
  // ("default_title" + a live burger backed by no session) until the content arrives.
  useEffect(() => {
    if (!withChrome) return;
    setResonanceLayout({
      title: content?.label ?? "",
      disableHeader: false,
      disableNav: false,
      // EXPLICIT slots, not project defaults: the preview has no authenticated
      // session behind it, so it must never render a live burger — including on
      // projects whose default layout puts the burger in the LEFT slot.
      headerLeft: ["logo"],
      headerRight: [],
      backgroundColor: content ? pageBackgroundOf(content.page_style) : "white",
    });
    return () => {
      // Back to the context defaults (ResonanceLayoutDefaultStateValue).
      setResonanceLayout({
        title: "default_title",
        headerLeft: undefined,
        headerRight: undefined,
        backgroundColor: "white",
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [withChrome, content]);

  useEffect(() => {
    let active = true;
    const load = async (): Promise<void> => {
      if (!token || !contentId || !studyId) {
        setInvalid(true);
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError(false);
        const { data } = await getApiContentPreview(studyId, Number(contentId), lang, token);
        if (!active) return;
        setContent(data);
      } catch (e) {
        if (!active) return;
        if (e?.response?.status === 401) setInvalid(true);
        else setError(true);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [token, contentId, studyId, lang]);

  // Preview each translation with the matching UI language (incl. TTS locale).
  const onLangChange = useCallback(
    (code: string): void => {
      setLang(code);
      if (code) i18n.changeLanguage(code);
    },
    [i18n]
  );

  const body = ((): ReactElement => {
    if (loading) {
      return (
        <Centered>
          <IonSpinner name="crescent" />
          <Muted>{t("questionnaire_preview_loading")}</Muted>
        </Centered>
      );
    }
    if (invalid) {
      return (
        <Centered>
          <Muted>{t("questionnaire_preview_invalid")}</Muted>
        </Centered>
      );
    }
    if (error || !content) {
      return (
        <Centered>
          <Muted>{t("questionnaire_preview_error")}</Muted>
        </Centered>
      );
    }
    return <DisplayContent editorJsData={content.content} />;
  })();

  return (
    <IonPage>
      <IonContent>
        <Banner>
          <BannerText>{t("questionnaire_preview_banner")}</BannerText>
          <Controls>
            <ChromeToggle>
              <input
                type="checkbox"
                checked={withChrome}
                onChange={(e) => setWithChrome(e.target.checked)}
              />
              {t("content_preview_chrome")}
            </ChromeToggle>
            <LangSelect
              value={lang}
              onChange={(e) => onLangChange(e.target.value)}
              aria-label={t("questionnaire_preview_lang_default")}
            >
              {PREVIEW_LANGS.map((l) => (
                <option key={l.code || "default"} value={l.code}>
                  {t(l.labelKey)}
                </option>
              ))}
            </LangSelect>
          </Controls>
        </Banner>
        {/* Same centered phone frame as QuestionnairePreview — the frame carries the
            authored page background so researchers judge the real page look. */}
        <Stage>
          {/* Chrome on: the nested ion-page paints the authored background itself —
              painting it on the frame TOO would composite translucent modes
              (primary-soft, gradient) twice and read darker than the real app. */}
          <PhoneFrame
            style={{
              background: withChrome ? "#ffffff" : pageBackgroundOf(content?.page_style),
            }}
          >
            {withChrome ? (
              <>
                {/* The REAL page shell: wave/flat/round header band, theme inks,
                    IonContent with the authored page background — exactly what a
                    participant's phone renders around this content. `standalone`
                    keeps this nested page out of the router's page stack (else it
                    gets stuck ion-page-invisible → white page). */}
                <ResonanceLayout standalone>{body}</ResonanceLayout>
                {/* The REAL tab bar: same web components, class (floating-tab-bar,
                    common.scss) and per-study tab set as Tabs.tsx — display-only,
                    no tab router behind it. */}
                <ion-tab-bar
                  className="floating-tab-bar"
                  selected-tab={studyTabs[0]?.id}
                  aria-hidden="true"
                >
                  {studyTabs.map((tab, index) => (
                    <ion-tab-button key={tab.id} tab={tab.id} selected={index === 0}>
                      {tab.iconSrc ? <IonIcon src={tab.iconSrc} /> : <IonIcon icon={tab.icon} />}
                      <IonLabel>{t(tab.labelKey)}</IonLabel>
                    </ion-tab-button>
                  ))}
                </ion-tab-bar>
              </>
            ) : (
              <FrameScroll $withChrome={false}>{body}</FrameScroll>
            )}
          </PhoneFrame>
        </Stage>
      </IonContent>
    </IonPage>
  );
};

export default ContentPreview;

const Banner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px 16px;
  background: #fff6e5;
  border-bottom: 1px solid #f1d9a8;
  position: sticky;
  top: 0;
  z-index: 5;
`;

const BannerText = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #8a6d3b;
`;

const LangSelect = styled.select`
  font-size: 13px;
  padding: 4px 8px;
  border: 1px solid #d9c08a;
  border-radius: 6px;
  background: #fff;
  color: #6b5424;
`;

const Stage = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 12px;
  background: #eceef1;
`;

/**
 * The frame hosts the REAL app components, so it only adapts them to a boxed
 * viewport: .ion-page (ResonanceLayout) is position:absolute and fills the frame
 * like a phone screen; the real .floating-tab-bar is position:FIXED (viewport) in
 * the app, so inside the frame it is re-anchored to absolute — everything else
 * (colors, shapes, data-attribute variants) is the components' own behavior.
 */
const PhoneFrame = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 460px;
  height: calc(100vh - 120px);
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
  overflow: hidden;

  ion-tab-bar.floating-tab-bar {
    position: absolute;
    /* Above the sibling ion-page (which paints an opaque page background). */
    z-index: 1000;
  }

  /* Display-only chrome: the dock has no tab router behind it in the preview. */
  ion-tab-bar.floating-tab-bar ion-tab-button {
    pointer-events: none;
  }
`;

/* Chrome-off mode: plain scroll box, like the bare content area. */
const FrameScroll = styled.div<{ $withChrome: boolean }>`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: ${({ $withChrome }) => ($withChrome ? "78px" : "0")};
`;

const ChromeToggle = styled.label`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 13px;
  font-weight: 600;
  color: #8a6d3b;
  cursor: pointer;
  user-select: none;
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Centered = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
  padding: 48px 16px;
`;

const Muted = styled.p`
  font-size: 14px;
  color: rgba(69, 80, 86, 0.75);
  margin: 0;
`;
